import React from 'react';
import PropTypes from 'prop-types';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from 'react-sortable-hoc';
import _ from 'lodash';
import underscore from 'underscore';
import { PICKUP, DELIVER } from 'constants/StopActions';
import SessionStore from 'stores/SessionStore';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import ShipmentWebAPIUtils, {
  handleStopEquipmentUpdateFail,
  handleStopEquipmentUpdateSuccess,
  getStopAudits,
} from 'utils/ShipmentWebAPIUtils';
import ShipmentMap from 'components/shipment/maps/ShipmentMap';

import AddStopModal from 'components/modules/shipments/stops/AddStopModal';
import EditLoadsModal from 'components/modules/shipments/load/EditLoadsModal';
import LocationModal from 'components/modules/shipments/stops/LocationModal';
import EditReferenceNumbersModal from 'components/modules/shipments/EditReferenceNumbersModal';
import Attempt from 'components/modules/shipments/Attempt';
import AddAppointmentWindow from 'components/ui/tailwind/modals/AddAppointmentModal';
import StopCompleteModal from 'components/modules/shipments/stops/StopCompleteModal';
import StopArrivedModal from 'components/modules/shipments/stops/StopArrivedModal';
import StopDocumentPreviewModal from 'components/modules/shipments/stops/StopDocumentPreviewModal';
import Stop from 'components/modules/shipments/stops/Stop';
import InfoIcon from 'components/v2/icons/InfoIcon';
import OutgateConfirmationModal from 'components/modules/shipments/OutgateConfirmationModal';
import ConfirmModal from 'components/ui/modals/ConfirmModal';

import {
  isStopMissingAppointment,
  getShipperStopDocumentRequirementsFromConfig,
  getUniqueCombinedStopDocumentRequirementsFromConfig,
  isStopCancelled,
} from 'helpers/stops';
import { isShipmentActive } from 'helpers/shipments';

import {
  isDrayageShipment,
  isIntermodalShipmentType,
} from 'helpers/shipment-types';
import { isEquipmentExemptLegType } from 'helpers/legs-helper';

import { Translation, useTranslation, withTranslation } from 'react-i18next';
import { trailerExists } from 'helpers/equipment';
import { shipperHasTag } from 'utils/formatters/shipments';
import { getYards } from 'utils/YardAPIUtils';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { Disclosure } from '@headlessui/react';
import { Button as ShadcnButton } from '@cargomatic/ui';
import { GripVertical, Plus } from 'lucide-react';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import { MapToggleButton } from 'components/design-system/atoms/MapToggleButton';
import withFlags from 'components/hoc/withFlags';
import {
  getMarketShipperRateWrapper,
  updateMarketShipperRateWrapper,
} from 'utils/PricingWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import SplitShipment from './SplitShipment';
import StopEquipmentModal from './stops/StopEquipmentModal';
import { ShipmentDistance } from 'components/v2/ShipmentDistance';
import { withLocaleContext } from '../../../providers/locale-settings-context';
import MarketShipperRateModel from './MarketShipperRateModel';

import Tooltip from 'components/ui/Tooltip';
const TOOLTIPS = {
  routeDetails: (
    <Translation>{(t) => t('SHIPMENT_ROUTE_DETAILS_TOOLTIP')}</Translation>
  ),
  intent: <Translation>{(t) => t('SHIPMENT_INTENT_TOOLTIP')}</Translation>,
  location: <Translation>{(t) => t('SHIPMENT_LOCATION_TOOLTIP')}</Translation>,
  windows: <Translation>{(t) => t('SHIPMENT_WINDOWS_TOOLTIP')}</Translation>,
  action: <Translation>{(t) => t('SHIPMENT_ACTION_TOOLTIP')}</Translation>,
  load: <Translation>{(t) => t('SHIPMENT_LOAD_TOOLTIP')}</Translation>,
  status: <Translation>{(t) => t('SHIPMENT_STATUS_TOOLTIP')}</Translation>,
};

const DragHandle = SortableHandle((props) => {
  const { t } = useTranslation();
  if (props && props.disabled) {
    return <div />;
  }
  return (
    <div className="cursor-pointer mr-2" title={t('CLICK_TO_DRAG')}>
      <GripVertical className="w-4 h-4" />
    </div>
  );
});

const SortableStop = SortableElement((props) => {
  return (
    <div className="relative">
      <Stop
        {...props}
        onActionSelect={(key, meta) => {
          props.onActionSelect(props.stop, key, meta);
        }}
      >
        <DragHandle
          disabled={
            !isIntermodalShipmentType(props.shipment) ||
            props?.disableStopActions === true
          }
        />
      </Stop>
    </div>
  );
});

const SortableStopsList = SortableContainer((props) => {
  const { stops, shipment, stopsToSplit, stopAudits } = props;

  const stopsCount = stops.length;

  const drayageShipment = isDrayageShipment(shipment);
  const intermodalShipmentType = isIntermodalShipmentType(shipment);
  const disabled =
    !intermodalShipmentType || props?.disableStopActions === true;

  const setYardName = (stop) => {
    if ('yardId' in stop && props.yards && props.yards.length) {
      const yard = props.yards.find((yard) => yard.id === stop.yardId);
      if (yard) {
        return SessionStore.isAdmin() ? yard.nameInternal : yard.nameExternal;
      }
    }

    return null;
  };

  const nextStop = underscore
    .chain(stops)
    .sortBy((s) => {
      return s.sequence;
    })
    .find((s) => {
      return !s.complete;
    })
    .value();

  const getPreviousStop = (currentStop) => {
    const activeStops = stops.filter((s) => !isStopCancelled(s));
    const sortedStops = underscore.sortBy(activeStops, (s) => s.sequence);
    const currentStopIndex = sortedStops.findIndex(
      (s) => s._id.toString() === currentStop._id.toString(),
    );
    if (currentStopIndex <= 0) {
      return null;
    }
    return sortedStops[currentStopIndex - 1];
  };

  const isStopCompleteActionDisabled = (
    stop,
    hasDrayageTrailerMovesTag = false,
  ) => {
    if (isStopCancelled(stop)) {
      return true;
    }

    const isCarrierMandatoryStopDocumentsEnabled = featureEnabled(
      CONFIGURED_FEATURES.CARRIER_MANDATORY_STOP_DOCUMENTS,
    );

    const previousStop = getPreviousStop(stop);
    // SSR - This case should go away when SSR is live
    // Always allow prior completed stops to provide documentation
    if (
      !featureEnabled(CONFIGURED_FEATURES.SSR) &&
      stop.missing_docs === true &&
      stop.off_site === false &&
      !stop.complete &&
      nextStop?._id === stop._id
    ) {
      return false;
    } else {
      // SSR - This case should be used when we go live
      // If not the next stop to complete, then no.
      if (isCarrierMandatoryStopDocumentsEnabled) {
        const hasNoDocs = stop.hasOwnProperty('missing_combined_docs')
          ? !stop.missing_combined_docs
          : !stop.missing_docs;

        if (nextStop?._id !== stop._id && hasNoDocs) {
          return true;
        }

        const hasDocs = stop.hasOwnProperty('missing_combined_docs')
          ? stop.missing_combined_docs
          : stop.missing_docs;

        if (
          !stop.complete &&
          hasDocs &&
          previousStop &&
          !previousStop?.complete
        ) {
          return true;
        }
      } else {
        // SSR - This case should be used when we go live
        // If not the next stop to complete, then no.
        if (nextStop?._id !== stop._id && !stop.missing_docs) {
          return true;
        }
        if (
          !stop.complete &&
          stop.missing_docs &&
          previousStop &&
          !previousStop?.complete
        ) {
          return true;
        }
      }
    }

    // If drayage or rail, we require a container on the stop
    // Chassis split and bobtail leg types don't require equipment
    if (intermodalShipmentType && !isEquipmentExemptLegType(shipment)) {
      if (hasDrayageTrailerMovesTag && !trailerExists(stop.equipments)) {
        return true;
      }
      if (
        !hasDrayageTrailerMovesTag &&
        (stop.equipments.length === 0 ||
          !_.find(stop.equipments, { _equipmentType: 'container' }))
      ) {
        return true;
      }
    }

    // Require appointments on drayage
    if (drayageShipment && isStopMissingAppointment(stop)) {
      return true;
    }

    return false;
  };

  return (
    <div className="flex flex-col space-y-6">
      {stops.map((s, i) => {
        const display_order_change = s.sequence > 1 ? '' : 'none';
        const audits = stopAudits.filter((audit) => audit.object === s._id);
        let actionsDisabled =
          !shipment.carrier ||
          !shipment.current ||
          props?.disableStopActions === true;
        if (i > 0 && stopsCount > 1) {
          const previousStop = stops[i - 1];
          if (!intermodalShipmentType) {
            actionsDisabled = actionsDisabled || !previousStop.complete;
          }
        }

        const hasDrayageTrailerMovesTag = shipperHasTag(
          shipment,
          'drayage_trailer_moves',
          shipment.shipment_type === 'drayage',
        );

        const completeActionDisabled = isShipmentActive(shipment)
          ? isStopCompleteActionDisabled(s, hasDrayageTrailerMovesTag)
          : true;

        const stopSelectedForSplit = stopsToSplit.some(
          (stop) => stop._id.toString() === s._id.toString(),
        );
        return (
          <>
            <SortableStop
              yardName={setYardName(s)}
              key={s._id}
              index={i}
              audits={audits}
              display_order_change={display_order_change}
              completeActionDisabled={completeActionDisabled}
              actionsDisabled={actionsDisabled}
              stop={s}
              disabled={disabled}
              displayOrderChange={display_order_change}
              showTotalWeight={i === 0}
              onStopSelect={() => props.onStopSelect(s)}
              selectedForSplit={stopSelectedForSplit}
              {...props}
            />
            <hr className="my-6" />
          </>
        );
      })}
    </div>
  );
});

const confirmationModalDetails = {
  type: 'outgate',
  title: 'ADDITIONAL_STOP_CONFIRM_TITLE',
  message: 'ADDITIONAL_STOP_CONFIRM_MESSAGE',
  confirmButtonText: 'YES',
  hideButtonText: 'NO',
};

class ShipmentRoute extends React.Component {
  static propTypes = {
    shipment: PropTypes.object,
    stops: PropTypes.array,
    waypoints: PropTypes.array,
    showWaypoints: PropTypes.bool,
    onStopLoadsModified: PropTypes.func.isRequired,
    onStopWindowModified: PropTypes.func.isRequired,
    onSaveLocationIntentChanges: PropTypes.func.isRequired,
    onSaveReferenceNumbers: PropTypes.func.isRequired,
    onUploadStopDocument: PropTypes.func.isRequired,
    onDeleteStopDocument: PropTypes.func.isRequired,
    onWaypointsSelect: PropTypes.func.isRequired,
    onCompleteStop: PropTypes.func.isRequired,
    onCopyStop: PropTypes.func.isRequired,
    onSortStops: PropTypes.func.isRequired,
    onStopTransition: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      showMap: props.flags?.showShipmentDetailsMap ?? false,
      showAddStopModal: false,
      expanded: false,
      showEditLocationIntentModal: false,
      showEditTimeWindowModal: false,
      showAttemptModal: false,
      showEditLoadsModal: false,
      showEditReferenceNumbersModal: false,
      showStopCompleteModal: false,
      showStopArrivedModal: false,
      showStopDocumentPreviewModal: false,
      showStopEquipmentModal: false,
      showConfirmationModal: false,
      showOutgateConfirmationModal: false,
      stop: {},
      confirmationModalDetails: {},
      stopsRemaining: [],
      stopsToSplit: [],
      splitShipmentSelected: false,
      documentsRefetch: undefined,
      yards: null,
      shipperStopDocumentRequirements: {},
      showActionModel: false,
      marketShipperRate: {},
      stopAudits: [],
    };
  }

  async componentDidMount() {
    const { shipment } = this.props;

    const {
      market: { market_id: marketId },
    } = shipment;
    const yardsResponse = await getYards('nameInternal', 'ascend', null, null, {
      id: null,
      marketId,
      nameInternal: null,
      status: null,
    });

    this.setState({ yards: yardsResponse.data.getYards.yards });

    if (
      featureEnabled(CONFIGURED_FEATURES.SSR) &&
      isIntermodalShipmentType(shipment)
    ) {
      try {
        const shipperId = shipment?.shipper?._id || shipment?.shipper;
        const isCarrierMandatoryStopDocumentsEnabled = featureEnabled(
          CONFIGURED_FEATURES.CARRIER_MANDATORY_STOP_DOCUMENTS,
        );
        const shipperStopDocumentRequirements =
          isCarrierMandatoryStopDocumentsEnabled
            ? await getUniqueCombinedStopDocumentRequirementsFromConfig(
                shipperId,
              )
            : await getShipperStopDocumentRequirementsFromConfig(shipperId);
        this.setState({ shipperStopDocumentRequirements });
      } catch (e) {
        console.error(
          `Error retrieving shipper stop document requirements: ${e.message}`,
        );
        this.setState({ shipperStopDocumentRequirements: {} });
      }
    }

    this.getStopsAudit();
  }

  onSortEnd = ({ oldIndex, newIndex }) =>
    this.props.onSortStops(oldIndex, newIndex);

  onCompleteStop(shipment, stop, file, meta) {
    const {
      shipper: { prompt_for_offsite, tags },
    } = shipment;
    const promptForOutgate =
      prompt_for_offsite || tags.includes('prompt_for_offsite');
    this.props.onCompleteStop(shipment, stop, file, meta);
    if (promptForOutgate) {
      this.handleOutgateConfirmationDetails(stop);
    }
  }

  handleOutgateConfirmationDetails = (stop) => {
    if (isDrayageShipment(this.props.shipment) && stop.action === 'pickup') {
      this.setState(
        {
          confirmationModalDetails: {
            ...confirmationModalDetails,
            stop,
          },
        },
        () => {
          this.setState({ showConfirmationModal: true });
        },
      );
    }
  };

  handleOutgateConfirmation = () => {
    if (this.state.confirmationModalDetails.type === 'outgate') {
      this.setState({
        showConfirmationModal: false,
        showOutgateConfirmationModal: true,
      });
    }
  };

  handleStopCompletion = (shipment, currentStop, file, meta) => {
    this.onCompleteStop(shipment, currentStop, file, meta);
  };

  handleCloseLocationModalg = () =>
    this.setState({ stop: null, showEditLocationIntentModal: false });

  handleCloseReferenceNumbersModal = () =>
    this.setState({ stop: null, showEditReferenceNumbersModal: false });

  handleSaveStopLoads = (stop, weight, loads, full_truckload, stop_only) => {
    const changes = {
      weight,
      loads,
      full_truckload: !!full_truckload,
      stop_only,
    };
    this.setState({ showEditLoadsModal: false }, () => {
      this.props.onStopLoadsModified(this.props.shipment, stop, changes);
    });
  };

  handleHideEquipmentModal = () => {
    this.setState({
      showStopEquipmentModal: false,
    });
  };

  handleUploadStopDocument = (stop, documentName, file, signatory) => {
    this.props.onUploadStopDocument(
      this.props.shipment,
      stop,
      documentName,
      file,
      signatory,
    );
  };
  handleDeleteStopDocument = (stop, key) => {
    this.props.onDeleteStopDocument(this.props.shipment, stop, key);
  };
  handleCloseAddStopModal = () => this.setState({ showAddStopModal: false });

  handleAddStopSave = (stopData) => {
    const {
      shipment: { _id: jobId },
    } = this.props;
    this.handleCloseAddStopModal();
    ShipmentsActionCreators.createStop(jobId, stopData);
  };

  handleOutgateStopCreation = (payload) => {
    this.setState(
      {
        showOutgateConfirmationModal: false,
      },
      async () => {
        await ShipmentWebAPIUtils.createOutgateShipmentStops(
          this.props.shipment._id,
          payload,
        );
      },
    );
  };

  handleSaveLocationIntentChanges = (stop, data) => {
    this.handleCloseLocationModalg();
    this.props.onSaveLocationIntentChanges(this.props.shipment, stop, data);
  };

  handleSaveReferenceNumbers = (data) => {
    const { stop } = this.state;

    this.handleCloseReferenceNumbersModal();
    this.props.onSaveReferenceNumbers(this.props.shipment, stop, data);
  };

  handleWindowChange(stop, data) {
    this.props.onStopWindowModified(this.props.shipment, stop, data);
    this.setState({
      showEditTimeWindowModal: false,
    });
  }

  handleStopEquipmentUpdateSuccess = (res) => {
    handleStopEquipmentUpdateSuccess(res);
  };

  handleStopEquipmentUpdateFail = (res) => {
    handleStopEquipmentUpdateFail(res);
  };

  handleStopTransition = (shipment, stop, event, meta) => {
    this.props.onStopTransition(shipment, stop, event, meta);
  };

  handleManageSelection(stop, key, meta) {
    /*
     * When we are ready to go bomb. Lets add additional actions.
     * <MenuItem eventKey="copy">Copy</MenuItem>
     * <MenuItem eventKey="cancel">Cancel</MenuItem>
     */
    switch (key) {
      case 'unstarted':
        this.handleStopTransition(this.props.shipment, stop, key, meta);
        break;
      case 'canceled':
        this.handleStopTransition(this.props.shipment, stop, key, meta);
        break;
      case 'copy':
        this.props.onCopyStop(this.props.shipment, stop, meta);
        break;
      case 'attempt':
        this.setState({ stop, showAttemptModal: true });
        break;
      case 'add_stop_off':
        this.setState({
          confirmationModalDetails: { stop },
          showOutgateConfirmationModal: true,
        });
        break;
      default:
    }
  }

  handleShipmentSplitReset = (close) => {
    return () => {
      close();
      this.setState({
        splitShipmentSelected: false,
        stopsToSplit: [],
      });
    };
  };

  isAddStopDisabled() {
    const { shipment } = this.props;
    const { current } = shipment;
    return (
      this.props?.disableStopActions === true ||
      !(
        current &&
        (isIntermodalShipmentType(shipment) ||
          SessionStore.hasMultiStopBookingRole())
      )
    );
  }

  onStopSelect = (selectedStop) => {
    const { stops } = this.props.shipment;
    this.setState((prevState) => {
      const stopSelected = prevState.stopsToSplit.find(
        (s) => s._id.toString() === selectedStop._id.toString(),
      );
      if (stopSelected) {
        const stopsToSplit = prevState.stopsToSplit.filter(
          (s) => s._id.toString() !== selectedStop._id.toString(),
        );
        const stopsToSplitIds = stopsToSplit.map((s) => s._id.toString());
        const stopsRemaining = stops.filter(
          (s) => !stopsToSplitIds.includes(s._id.toString()),
        );
        return {
          stopsRemaining,
          stopsToSplit,
          splitShipmentSelected: stopsToSplit.length > 0,
        };
      }
      const stopsToSplit = [...prevState.stopsToSplit, selectedStop];
      const stopsToSplitIds = stopsToSplit.map((s) => s._id.toString());
      const stopsRemaining = stops.filter(
        (s) => !stopsToSplitIds.includes(s._id.toString()),
      );
      return {
        stopsRemaining,
        stopsToSplit,
      };
    });
  };

  getStopsAudit = async () => {
    const { stops } = this.props;
    try {
      const results = await Promise.all(
        (stops || []).map((stop) => {
          return getStopAudits(stop._id);
        }),
      );
      this.setState({ stopAudits: _.flatten(results) });
      Promise.resolve();
    } catch (_e) {
      showToast(
        this.props.t('STOP_AUDIT_FETCH_FAILED'),
        this.props.t('STOP_AUDIT_FETCH_FAILED'),
        'error',
      );
      Promise.reject();
    }
  };

  refreshStopsAudit = async () => {
    try {
      await this.getStopsAudit().catch((e) => {
        throw e;
      });
      showToast(
        this.props.t('STOP_AUDIT_UPDATED_SUCCESSFULLY'),
        this.props.t('STOP_AUDIT_UPDATED_SUCCESSFULLY'),
        'success',
      );
    } catch (e) {
      return console.log('Error updating Stop Audits', e);
    }
  };

  rateCheck = async (shipperId, marketId) => {
    let marketShipperRate;
    const query = {
      shipperId,
      marketId,
    };
    const response = await getMarketShipperRateWrapper(query);
    if (response.rows[0] && response.rows[0]?.rate) {
      marketShipperRate = response.rows[0];
      this.setState((prev) => ({ ...prev, marketShipperRate }));
    }

    return marketShipperRate;
  };

  onShowStopCompleteModal = async (stop, documentsRefetch) => {
    const { shipment } = this.props;
    if (stop.yardId && stop.off_site) {
      const marketRate = await this.rateCheck(
        shipment.shipper._id,
        shipment.market.market_id,
      );
      if (!marketRate) {
        this.setState((prevState) => {
          return {
            ...prevState,
            stop,
            documentsRefetch,
            showActionModel: true,
          };
        });
        return;
      }
    }
    this.setState({
      stop,
      showStopCompleteModal: true,
      documentsRefetch,
    });
  };

  handleShowMarketShipperRateModel = (showModal) => {
    this.setState((prevState) => {
      return {
        ...prevState,
        showActionModel: showModal,
      };
    });
  };

  saveMarketShipperRateHandler = async (rate, freeDays, shipment) => {
    const { error, data } = await updateMarketShipperRateWrapper({
      shipperId: shipment.shipper.id,
      marketId: shipment.market.market_id,
      rate,
      freeDays,
    });
    if (error) {
      showToast(
        this.props.t('UPDATE_MARKET_SHIPPER_RATE_ERROR'),
        this.props.t('UPDATE_MARKET_SHIPPER_RATE_FAILED_WITH_ERROR', {
          error: error.message,
        }),
        'error',
      );
      return;
    }
    showToast(
      this.props.t('MARKET_SHIPPER_RATE_UPDATED_SUCCESSFULLY'),
      this.props.t('MARKET_SHIPPER_RATE_UPDATED_SUCCESSFULLY'),
      'success',
    );
    this.handleShowMarketShipperRateModel(false);
    this.setState({ marketShipperRate: data[0] }, () => {
      this.setState((prev) => ({ ...prev, showStopCompleteModal: true }));
    });
  };

  renderStops() {
    const shipment = this.props.shipment;

    let stops = _.sortBy(this.props.stops || [], (s) => s.sequence);

    return (
      <SortableStopsList
        yards={this.state.yards}
        stops={stops}
        stopAudits={this.state.stopAudits}
        shipment={shipment}
        disableStopActions={this.props?.disableStopActions === true}
        onSortEnd={this.onSortEnd}
        onShowEditEquipmentModal={(stop) => {
          this.setState({ stop, showStopEquipmentModal: true });
        }}
        onShowEditLocationIntentModal={(stop) => {
          this.setState({ stop, showEditLocationIntentModal: true });
        }}
        onShowEditLoadsModal={(stop) => {
          this.setState({ stop, showEditLoadsModal: true });
        }}
        onShowEditTimeWindowModal={(stop) => {
          this.setState({ stop, showEditTimeWindowModal: true });
        }}
        onShowEditReferenceNumbersModal={(stop) => {
          this.setState({ stop, showEditReferenceNumbersModal: true });
        }}
        onUploadStopDocument={this.handleUploadStopDocument}
        onDeleteStopDocument={this.handleDeleteStopDocument}
        onActionSelect={(stop, key, meta) => {
          this.handleManageSelection(stop, key, meta);
        }}
        expanded={this.state.expanded}
        onStopTransition={this.handleStopTransition}
        useDragHandle={isIntermodalShipmentType(shipment)}
        onShowStopCompleteModal={this.onShowStopCompleteModal}
        onShowStopArrivedModal={(stop) => {
          this.setState({ stop, showStopArrivedModal: true });
        }}
        onShowStopDocumentPreviewModal={(stop) => {
          this.setState({ stop, showStopDocumentPreviewModal: true });
        }}
        refreshStopsAudit={this.refreshStopsAudit}
        onStopSelect={
          this.props?.disableStopActions === true ? () => {} : this.onStopSelect
        }
        stopsToSplit={
          this.props?.disableStopActions === true ? [] : this.state.stopsToSplit
        }
      />
    );
  }

  getStopAction = () => {
    const { stop } = this.state;
    if (stop && !_.isEmpty(stop)) {
      return stop.action === PICKUP ? PICKUP : DELIVER;
    }
    return null;
  };

  renderModals() {
    const { t, shipment } = this.props;
    const stop = this.state.stop || {};
    const reference_numbers = stop.reference_numbers || [];
    const isShipmentType = isIntermodalShipmentType(shipment);
    return (
      <div>
        <AddStopModal
          show={this.state.showAddStopModal}
          shipment={this.props.shipment}
          onClose={this.handleCloseAddStopModal}
          onSave={this.handleAddStopSave}
        />
        <LocationModal
          show={this.state.showEditLocationIntentModal}
          shipment={this.props.shipment}
          shipment_type={this.props.shipment.shipment_type}
          stop={stop}
          onClose={this.handleCloseLocationModalg}
          onSave={this.handleSaveLocationIntentChanges}
        />
        <EditLoadsModal
          show={this.state.showEditLoadsModal}
          onUpdateLoad={this.handleSaveStopLoads}
          onHide={() => this.setState({ showEditLoadsModal: false })}
          stop={stop}
          shipment={this.props.shipment}
        />

        <EditReferenceNumbersModal
          show={this.state.showEditReferenceNumbersModal}
          reference_numbers={reference_numbers}
          isIntermodalShipmentType={isShipmentType}
          onClose={this.handleCloseReferenceNumbersModal}
          onSave={this.handleSaveReferenceNumbers}
          shipper={this.props.shipment.shipper}
          getStopAction={this.getStopAction}
          isStop
          entityType="shipment"
          page="ShipmentRoute"
        />

        <Attempt
          show={this.state.showAttemptModal}
          onHideModal={() => this.setState({ showAttemptModal: false })}
          shipment={this.props.shipment}
          stop={stop}
          onClose={this.props.onStopTransition}
        />

        {this.state.showEditTimeWindowModal && (
          <AddAppointmentWindow
            show={this.state.showEditTimeWindowModal}
            stop={stop}
            shipment={this.props.shipment}
            onSave={(data) => this.handleWindowChange(this.state.stop, data)}
            onHide={() => this.setState({ showEditTimeWindowModal: false })}
          />
        )}
        {this.state.showActionModel && (
          <MarketShipperRateModel
            showModal={this.state.showActionModel}
            setShowActionModel={this.handleShowMarketShipperRateModel}
            shipment={this.props.shipment}
            onSave={this.saveMarketShipperRateHandler}
          />
        )}

        {this.state.showStopCompleteModal && (
          <StopCompleteModal
            show={this.state.showStopCompleteModal}
            shipmentId={this.props.shipment._id}
            stopId={stop._id}
            onStopComplete={this.handleStopCompletion}
            onStopEquipmentUpdateSuccess={this.handleStopEquipmentUpdateSuccess}
            onStopEquipmentUpdateFail={this.handleStopEquipmentUpdateFail}
            onHide={() => this.setState({ showStopCompleteModal: false })}
            isCarrier={false}
            yards={this.state.yards} // use this to match stops to yards
            shipperStopDocumentRequirements={
              this.state.shipperStopDocumentRequirements
            }
            marketShipperRate={this.state.marketShipperRate}
          />
        )}
        {this.state.showStopArrivedModal && (
          <StopArrivedModal
            stop={this.state.stop}
            shipment={this.props.shipment}
            show={this.state.showStopArrivedModal}
            onHide={() => this.setState({ showStopArrivedModal: false })}
            onStopArrived={this.handleStopTransition}
          />
        )}

        {this.state.showStopDocumentPreviewModal && (
          <StopDocumentPreviewModal
            shipment={this.props.shipment}
            show={this.state.showStopDocumentPreviewModal}
            stop={this.state.stop}
            onStopComplete={this.handleStopCompletion}
            onHide={() =>
              this.setState({ showStopDocumentPreviewModal: false })
            }
          />
        )}
        {this.state.showStopEquipmentModal && (
          <StopEquipmentModal
            show={this.state.showStopEquipmentModal}
            shipmentId={this.props.shipment._id}
            stopId={stop._id}
            onStopEquipmentUpdateSuccess={this.handleStopEquipmentUpdateSuccess}
            onStopEquipmentUpdateFail={this.handleStopEquipmentUpdateFail}
            onHide={this.handleHideEquipmentModal}
            isCarrier={false}
          />
        )}

        {this.state.showConfirmationModal && (
          <ConfirmModal
            show={this.state.showConfirmationModal}
            title={t(this.state.confirmationModalDetails.title)}
            message={t(this.state.confirmationModalDetails.message)}
            affirmative={t(
              this.state.confirmationModalDetails.confirmButtonText,
            )}
            hideButtonText={t(
              this.state.confirmationModalDetails.hideButtonText,
            )}
            onConfirm={this.handleOutgateConfirmation}
            onHide={() => this.setState({ showConfirmationModal: false })}
          />
        )}

        {this.state.showOutgateConfirmationModal && (
          <OutgateConfirmationModal
            show={this.state.showOutgateConfirmationModal}
            onCancel={() =>
              this.setState({
                showOutgateConfirmationModal: false,
              })
            }
            onSave={this.handleOutgateStopCreation}
            stop={this.state.confirmationModalDetails.stop}
          />
        )}
      </div>
    );
  }

  render() {
    const { t, localeData } = this.props;
    return (
      <CargomaticCollapsibleCard
        id="shipment-route-details"
        header={() => (
          <div className="flex items-center gap-2">
            <span>{t('ROUTE_DETAILS')}</span>
            <Tooltip content={TOOLTIPS.routeDetails}>
              <InfoIcon size="medium" />
            </Tooltip>
            <MapToggleButton
              showMap={this.state.showMap}
              className="ml-auto"
              onClick={(e) => {
                e.stopPropagation();
                this.setState({ showMap: !this.state.showMap });
              }}
            />
          </div>
        )}
      >
        {this.state.showMap && (
          <div className="mb-4 -mx-6">
            <ShipmentMap
              height={450}
              shipment={this.props.shipment}
              waypoints={this.props.waypoints}
              onWaypointsSelect={this.props.onWaypointsSelect}
              showWaypoints={this.props.showWaypoints}
            />
          </div>
        )}

        <div>
          <Disclosure>
            <div>
              <Disclosure.Button className="mb-2 block text-blue underline hover:text-blue-700">
                {({ open }) => (
                  <>
                    {open && <span>{t('COLLAPSE_ALL')}</span>}
                    {!open && <span>{t('EXPAND_ALL')}</span>}
                  </>
                )}
              </Disclosure.Button>
            </div>
            <div className="overflow-x-auto">
              <div className="grid grid-cols-12 gap-4 min-w-[1100px]">
                <div className="col-span-2 2xl:col-span-1 flex items-center space-x-1">
                  <b>{t('INTENT')} </b>
                  <Tooltip content={TOOLTIPS.intent}>
                    <InfoIcon size="medium" />
                  </Tooltip>
                </div>
                <div className="col-span-2 2xl:col-span-3">
                  <div className="flex items-center space-x-1">
                    <b>{t('LOCATION')} </b>
                    <Tooltip content={TOOLTIPS.location}>
                      <InfoIcon size="medium" />
                    </Tooltip>
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center space-x-1">
                    <b>{t('WINDOW')} </b>
                    <Tooltip content={TOOLTIPS.windows}>
                      <InfoIcon size="medium" />
                    </Tooltip>
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center space-x-1">
                    <b>{t('LOAD_DETAILS')} </b>
                    <Tooltip content={TOOLTIPS.load}>
                      <InfoIcon size="medium" />
                    </Tooltip>
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center space-x-1">
                    <b>{t('STATUS')} </b>
                    <Tooltip content={TOOLTIPS.status}>
                      <InfoIcon size="medium" />
                    </Tooltip>
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="flex items-center space-x-1">
                    <b>{t('ACTIONS')} </b>
                    <Tooltip content={TOOLTIPS.action}>
                      <InfoIcon size="medium" />
                    </Tooltip>
                  </div>
                </div>
              </div>
              {this.renderStops()}
            </div>

            <ShipmentDistance
              distance={this.props.shipment.distance}
              routingType={this.props.shipment.routing_type}
              routingConfiguration={this.props.shipment.routing_configuration}
              toUnit={localeData.distance_units}
            />
          </Disclosure>
          <Disclosure>
            <div className="flex justify-end gap-2 border-t bg-muted/40 px-6 py-3">
              <Disclosure.Button
                as={ShadcnButton}
                variant="outline"
                size="sm"
                disabled={
                  this.props.disableStopActions ||
                  !this.state.stopsToSplit.length ||
                  this.state.stopsToSplit.length ===
                    this.props.shipment.stops.length
                }
                onClick={() =>
                  this.setState({ splitShipmentSelected: true }, () =>
                    window.scrollBy(0, 150),
                  )
                }
              >
                {t('SPLIT_STOPS')}
              </Disclosure.Button>
              <ShadcnButton
                size="sm"
                disabled={
                  this.props.disableStopActions || this.isAddStopDisabled()
                }
                onClick={() => {
                  this.setState({ showAddStopModal: true });
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('ADD_STOPS')}
              </ShadcnButton>
            </div>
            <Disclosure.Panel>
              {({ close }) => (
                <div className="border-t-2 border-black py-4">
                  <SplitShipment
                    shipmentId={this.props.shipment._id}
                    originJob={{
                      stops: this.state.stopsRemaining,
                      carrier: this.props.shipment.carrier,
                    }}
                    newJob={{ stops: this.state.stopsToSplit }}
                    onCancel={this.handleShipmentSplitReset(close)}
                  />
                </div>
              )}
            </Disclosure.Panel>
          </Disclosure>
        </div>
        {this.renderModals()}
      </CargomaticCollapsibleCard>
    );
  }
}

export default withTranslation()(withLocaleContext(withFlags(ShipmentRoute)));
